var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { action, computed, makeObservable, observable } from 'mobx';
import { changeLanguage } from '@data/other/i18n';
import { DefaultLocale, } from '@data/types/common';
import { system } from '@config/system';
import { isInIframe } from '@business/utils/window';
import { languageToLocale } from '@business/utils/language-locale';
import { persistentPropertyStorage } from '../PersistentPropertyStorage';
export default class Environment {
    constructor() {
        Object.defineProperty(this, "currency", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'usd'
        });
        Object.defineProperty(this, "currencyRate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "immersiveExperienceSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "initialUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "language", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'en'
        });
        Object.defineProperty(this, "locale", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: DefaultLocale
        });
        // Property code don't need to be observable because it's defined once
        Object.defineProperty(this, "propertyCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        Object.defineProperty(this, "showImmersiveSessionResetNotice", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isHostedExperience", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => system.isHostedExperience && !this.immersiveExperienceSettings
        });
        Object.defineProperty(this, "isImmersiveExperience", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => system.isImmersiveExperience && !!this.immersiveExperienceSettings
        });
        Object.defineProperty(this, "isImmersiveExperiencePopupMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => system.isImmersiveExperience &&
                !!this.immersiveExperienceSettings &&
                this.immersiveExperienceSettings.mode === 'popup'
        });
        Object.defineProperty(this, "isImmersiveExperienceStandardMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => system.isImmersiveExperience &&
                !!this.immersiveExperienceSettings &&
                this.immersiveExperienceSettings.mode === 'standard'
        });
        Object.defineProperty(this, "setImmersiveExperienceSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (immersiveExperienceSettings) => {
                this.immersiveExperienceSettings = immersiveExperienceSettings;
            }
        });
        makeObservable(this);
    }
    get bookingEngineSource() {
        if (this.isImmersiveExperience()) {
            return this.isImmersiveExperiencePopupMode()
                ? 'immersive-popup'
                : 'immersive-fullpage';
        }
        if (isInIframe()) {
            const urlToParse = this.initialUrl || window.location.href;
            const parsedUrl = new URL(urlToParse);
            const isPopup = parsedUrl.searchParams.get('mode') === 'popup';
            return isPopup ? 'iframe-popup' : 'iframe-fullpage';
        }
        return 'hosted';
    }
    dismissImmersiveSessionResetNotice() {
        this.showImmersiveSessionResetNotice = false;
    }
    get isLoadingCurrencyRate() {
        return this.currencyRate === 0;
    }
    load() {
        const loadedData = persistentPropertyStorage.getItem(this.storageKey);
        if (loadedData) {
            const parsedData = JSON.parse(loadedData);
            this.currency = parsedData.currency;
            this.currencyRate = parsedData.currencyRate;
            this.immersiveExperienceSettings = parsedData.immersiveExperienceSettings;
            this.language = parsedData.language;
            this.locale = languageToLocale(this.language);
        }
    }
    save() {
        persistentPropertyStorage.setItem(this.storageKey, JSON.stringify(this));
    }
    setCurrency(currency) {
        this.currency = currency;
    }
    setCurrencyRate(currencyRate) {
        this.currencyRate = currencyRate;
    }
    setInitialUrl(url) {
        this.initialUrl = url;
    }
    setLanguage(language) {
        return __awaiter(this, void 0, void 0, function* () {
            if (system.isHostedExperience) {
                document.documentElement.lang = language;
            }
            this.language = language;
            this.locale = languageToLocale(language);
            yield changeLanguage(language);
        });
    }
    setPropertyCode(code) {
        this.propertyCode = code;
    }
    setShowImmersiveSessionResetNotice(value) {
        this.showImmersiveSessionResetNotice = value;
    }
    get storageKey() {
        return 'environment';
    }
}
__decorate([
    observable,
    __metadata("design:type", String)
], Environment.prototype, "currency", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Environment.prototype, "currencyRate", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Environment.prototype, "immersiveExperienceSettings", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Environment.prototype, "initialUrl", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], Environment.prototype, "language", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], Environment.prototype, "locale", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Environment.prototype, "showImmersiveSessionResetNotice", void 0);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Environment.prototype, "dismissImmersiveSessionResetNotice", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Environment.prototype, "isLoadingCurrencyRate", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], Environment.prototype, "setCurrency", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number]),
    __metadata("design:returntype", void 0)
], Environment.prototype, "setCurrencyRate", null);
__decorate([
    action.bound,
    __metadata("design:type", Object)
], Environment.prototype, "setImmersiveExperienceSettings", void 0);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], Environment.prototype, "setInitialUrl", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", Promise)
], Environment.prototype, "setLanguage", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], Environment.prototype, "setPropertyCode", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], Environment.prototype, "setShowImmersiveSessionResetNotice", null);
